import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { BackgroundUploader } from '@/components/upload/background-uploader';
import { supabase } from '@/lib/supabaseClient';
import { deleteUploadRetry, getUploadRetry, updateUploadRetry } from '@/lib/uploadRetryStore';

// 🆕 Global storage for queued upload data (accessed from step-schedule.tsx)
declare global {
  interface Window {
    queuedUploadsData?: Map<string, any>;
  }
}

interface UploadItem {
  id: string;
  title: string;
  platforms: ('YouTube' | 'Instagram' | 'TikTok')[];
  status: 'uploading' | 'completed' | 'skipped' | 'failed' | 'queued';
  progress?: number;
  timeLeft?: string;
  estimatedTime?: string;
  startedAt?: number;
  publishTiming?: 'now' | 'schedule';
  scheduledAt?: string;
  lastEstimatedSeconds?: number;
  cancelledPlatforms?: string[];
}

interface UploadQueueContextType {
  uploads: UploadItem[];
  addUpload: (upload: UploadItem) => void;
  addDebugUpload: (status: 'failed' | 'cancelled') => void;
  updateUpload: (id: string, updates: Partial<UploadItem>) => void;
  removeUpload: (id: string) => void;
  retryUpload: (id: string) => void;
  clearCompleted: () => void;
}

const UploadQueueContext = createContext<UploadQueueContextType | undefined>(undefined);

function formatRemainingTime(seconds: number) {
  const roundedSeconds = Math.max(1, Math.ceil(seconds));
  if (roundedSeconds < 60) return `${roundedSeconds}s`;

  const minutes = Math.floor(roundedSeconds / 60);
  const remainingSeconds = roundedSeconds % 60;
  return remainingSeconds === 0
    ? `${minutes}m`
    : `${minutes}m ${remainingSeconds}s`;
}

function getEstimatedSeconds(
  progress: number | undefined,
  startedAt: number | undefined,
  previousEstimate?: number,
  now = Date.now(),
) {
  if (!progress || progress <= 0 || progress >= 100 || !startedAt) return undefined;

  const elapsedSeconds = (now - startedAt) / 1000;
  if (elapsedSeconds < 2) return undefined;

  const rawEstimate = elapsedSeconds * ((100 - progress) / progress);
  const smoothedEstimate = previousEstimate === undefined
    ? rawEstimate
    : (previousEstimate * 0.7) + (rawEstimate * 0.3);
  const boundedEstimate = previousEstimate === undefined
    ? smoothedEstimate
    : Math.min(previousEstimate, smoothedEstimate);

  return boundedEstimate;
}

function getEstimatedTime(
  progress: number | undefined,
  startedAt: number | undefined,
  previousEstimate?: number,
  now = Date.now(),
) {
  const seconds = getEstimatedSeconds(progress, startedAt, previousEstimate, now);
  return seconds === undefined ? undefined : formatRemainingTime(seconds);
}

export function UploadQueueProvider({ children }: { children: React.ReactNode }) {
  const isDevelopment = import.meta.env.DEV;

  // Get current userId synchronously from localStorage
  const getCurrentUserId = () => {
    const authTokenKey = Object.keys(localStorage).find(key => 
      key.startsWith('sb-') && key.endsWith('-auth-token')
    );
    
    if (authTokenKey) {
      try {
        const authData = JSON.parse(localStorage.getItem(authTokenKey) || '{}');
        return authData?.user?.id || null;
      } catch {
        return null;
      }
    }
    return null;
  };

  const [currentUserId, setCurrentUserId] = useState<string | null>(getCurrentUserId);
  
  const [uploads, setUploads] = useState<UploadItem[]>(() => {
    // Load queue for THIS user only (scoped by userId)
    const userId = getCurrentUserId();
    if (!userId) return [];
    
    try {
      const queueKey = `fortuna-upload-queue-${userId}`;
      const saved = localStorage.getItem(queueKey);
      console.log(`📦 Loading queue for user ${userId.substring(0, 8)}:`, saved ? `${JSON.parse(saved).length} items` : 'empty');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });
  const [showUploader, setShowUploader] = useState(true);

  // Clean up old non-scoped keys on mount
  useEffect(() => {
    // Remove old non-scoped keys that might exist
    localStorage.removeItem('fortuna-upload-queue');
    localStorage.removeItem('fortuna-last-user-id');
  }, []);

  // Update userId when auth changes
  useEffect(() => {
    const checkAuth = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      const newUserId = user?.id || null;
      
      if (newUserId !== currentUserId) {
        console.log(`🔄 User changed: ${currentUserId?.substring(0, 8)} → ${newUserId?.substring(0, 8)}`);
        setCurrentUserId(newUserId);
        
        // Load new user's queue
        if (newUserId) {
          const queueKey = `fortuna-upload-queue-${newUserId}`;
          try {
            const saved = localStorage.getItem(queueKey);
            setUploads(saved ? JSON.parse(saved) : []);
            console.log(`📦 Loaded queue for new user`);
          } catch {
            setUploads([]);
          }
        } else {
          setUploads([]);
        }
      }
    };

    checkAuth();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(() => {
      checkAuth();
    });

    return () => subscription.unsubscribe();
  }, [currentUserId]);

  // Define callbacks FIRST before useEffects
  const addUpload = useCallback((upload: UploadItem) => {
    setUploads(prev => [...prev, upload]);
    setShowUploader(true);
  }, []);

  const addDebugUpload = useCallback((status: 'failed' | 'cancelled') => {
    if (!import.meta.env.DEV) return;

    const uploadId = `debug-${status}-${Date.now()}`;
    const debugUpload: UploadItem = {
      id: uploadId,
      title: status === 'failed' ? 'Debug failed upload' : 'Debug cancelled upload',
      platforms: ['YouTube'],
      status: status === 'cancelled' ? 'skipped' : 'failed',
      progress: status === 'cancelled' ? 42 : 68,
      timeLeft: status === 'cancelled' ? 'Cancelled by user' : 'Upload failed',
      cancelledPlatforms: status === 'cancelled' ? ['YouTube'] : undefined,
    };

    setUploads(prev => [...prev, debugUpload]);
    setShowUploader(true);
  }, []);

  const updateUpload = useCallback((id: string, updates: Partial<UploadItem>) => {
    setUploads(prev => prev.map(upload => {
      if (upload.id !== id) return upload;

      const nextUpload = { ...upload, ...updates };
      const estimatedTime = getEstimatedTime(
        nextUpload.progress,
        nextUpload.startedAt,
        nextUpload.lastEstimatedSeconds,
      );
      return {
        ...nextUpload,
        estimatedTime,
        lastEstimatedSeconds: getEstimatedSeconds(
          nextUpload.progress,
          nextUpload.startedAt,
          nextUpload.lastEstimatedSeconds,
        ) ?? nextUpload.lastEstimatedSeconds,
      };
    }));
  }, []);

  const removeUpload = useCallback((id: string) => {
    setUploads(prev => prev.filter(upload => upload.id !== id));
  }, []);

  const retryUpload = useCallback(async (id: string) => {
    if (id.startsWith('debug-')) return;

    const record = await getUploadRetry(id);
    if (!record) {
      updateUpload(id, { status: 'failed', timeLeft: 'Upload data unavailable' });
      return;
    }

    const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
    if (sessionError || !sessionData.session) {
      updateUpload(id, { status: 'failed', timeLeft: 'Session expired. Please sign in again.' });
      return;
    }

    const startedAt = Date.now();
    updateUpload(id, {
      status: 'uploading',
      progress: 0,
      startedAt,
      timeLeft: 'Retrying...',
      estimatedTime: undefined,
      lastEstimatedSeconds: undefined,
    });

    const { processUpload } = await import('@/lib/processUpload');
    processUpload(id, record.data, record.userId, sessionData.session, {
      onProgress: (progress, message) => updateUpload(id, { progress, timeLeft: message }),
      onCheckpoint: (updates) => updateUploadRetry(id, { data: updates }),
      onComplete: () => {
        updateUpload(id, { status: 'completed', progress: 100, estimatedTime: undefined });
        void deleteUploadRetry(id);
        window.dispatchEvent(new Event('refresh-uploads'));
      },
      onError: (error) => updateUpload(id, { status: 'failed', timeLeft: error, estimatedTime: undefined }),
    });
  }, [updateUpload]);

  const clearCompleted = useCallback(() => {
    setUploads(prev => prev.filter(upload => upload.status !== 'completed'));
  }, []);

  // Save to user-scoped localStorage whenever uploads change
  useEffect(() => {
    if (currentUserId) {
      const queueKey = `fortuna-upload-queue-${currentUserId}`;
      try {
        localStorage.setItem(queueKey, JSON.stringify(uploads));
      } catch (error) {
        console.error('Failed to save uploads to localStorage:', error);
      }
    }
  }, [uploads, currentUserId]);

  useEffect(() => {
    const timer = setInterval(() => {
      const now = Date.now();
      setUploads(prev => prev.map(upload => {
        if (upload.status !== 'uploading' || !upload.startedAt) return upload;

        const estimatedTime = getEstimatedTime(
          upload.progress,
          upload.startedAt,
          upload.lastEstimatedSeconds,
          now,
        );

        return {
          ...upload,
          estimatedTime,
          lastEstimatedSeconds: getEstimatedSeconds(
            upload.progress,
            upload.startedAt,
            upload.lastEstimatedSeconds,
            now,
          ) ?? upload.lastEstimatedSeconds,
        };
      }));
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // Auto-clear completed uploads after 15 minutes
  useEffect(() => {
    const timer = setInterval(() => {
      setUploads(prev => {
        const now = Date.now();
        const fifteenMinutes = 15 * 60 * 1000;
        
        return prev.filter(upload => {
          if (upload.status === 'completed') {
            // Extract timestamp from upload ID (format: upload-{timestamp})
            const uploadTime = parseInt(upload.id.split('-')[1]);
            if (isNaN(uploadTime)) return true; // Keep if we can't parse timestamp
            
            // Remove if older than 15 minutes
            return (now - uploadTime) < fifteenMinutes;
          }
          return true; // Keep non-completed uploads
        });
      });
    }, 60000); // Check every minute

    return () => clearInterval(timer);
  }, []);

  // 🆕 QUEUE PROCESSOR: Automatically start next queued upload when current finishes
  useEffect(() => {
    const hasActiveUpload = uploads.some(u => u.status === 'uploading');
    const queuedUploads = uploads.filter(u => u.status === 'queued');
    
    // If no active upload and there are queued uploads, start the next one
    if (!hasActiveUpload && queuedUploads.length > 0) {
      const nextUpload = queuedUploads[0];
      console.log('🎬 Queue Processor: Starting next upload:', nextUpload.id, nextUpload.title);
      
      // Get stored upload data from global Map (set by step-schedule.tsx)
      const storedData = window.queuedUploadsData?.get(nextUpload.id);
      
      if (!storedData) {
        console.error('❌ No stored data found for queued upload:', nextUpload.id);
        setUploads(prev => prev.map(u => 
          u.id === nextUpload.id ? { ...u, status: 'failed', timeLeft: 'Upload data not found' } : u
        ));
        return;
      }
      
      try {
        const { data, userId, session } = storedData;
        console.log('✅ Found stored upload data, starting upload processing...');
        const uploadStartedAt = Date.now();
        
        // Update status to uploading
        setUploads(prev => prev.map(u => 
          u.id === nextUpload.id ? {
            ...u,
            status: 'uploading',
            progress: 0,
            timeLeft: 'Starting...',
            startedAt: uploadStartedAt,
          } : u
        ));
        
        // Import and call processUpload
        import('@/lib/processUpload').then(({ processUpload }) => {
          processUpload(nextUpload.id, data, userId, session, {
            onProgress: (progress, message) => {
              console.log(`📊 [${nextUpload.id}] Progress: ${progress}% - ${message}`);
              setUploads(prev => prev.map(u => {
                if (u.id !== nextUpload.id) return u;

                const estimatedSeconds = getEstimatedSeconds(
                  progress,
                  u.startedAt,
                  u.lastEstimatedSeconds,
                );

                return {
                  ...u,
                  progress,
                  timeLeft: message,
                  estimatedTime: estimatedSeconds === undefined
                    ? undefined
                    : formatRemainingTime(estimatedSeconds),
                  lastEstimatedSeconds: estimatedSeconds ?? u.lastEstimatedSeconds,
                };
              }));
            },
            onComplete: () => {
              console.log(`✅ [${nextUpload.id}] Upload completed successfully`);
              setUploads(prev => prev.map(u => 
                u.id === nextUpload.id ? {
                  ...u,
                  status: 'completed',
                  progress: 100,
                  estimatedTime: undefined,
                } : u
              ));
              
              // Clean up stored data
              window.queuedUploadsData?.delete(nextUpload.id);
              void deleteUploadRetry(nextUpload.id);
              
              // Dispatch event for dashboard refresh
              window.dispatchEvent(new Event('refresh-uploads'));
            },
            onCheckpoint: (updates) => updateUploadRetry(nextUpload.id, { data: updates }),
            onError: (error) => {
              console.error(`❌ [${nextUpload.id}] Upload failed:`, error);
              setUploads(prev => prev.map(u => 
                u.id === nextUpload.id ? {
                  ...u,
                  status: 'failed',
                  timeLeft: error,
                  estimatedTime: undefined,
                } : u
              ));
              
              // Clean up stored data
              window.queuedUploadsData?.delete(nextUpload.id);
            }
          });
        }).catch(err => {
          console.error('❌ Failed to load processUpload:', err);
          setUploads(prev => prev.map(u => 
            u.id === nextUpload.id ? { ...u, status: 'failed', timeLeft: 'Failed to start upload' } : u
          ));
        });
        
      } catch (err) {
        console.error('❌ Failed to process stored upload data:', err);
        setUploads(prev => prev.map(u => 
          u.id === nextUpload.id ? { ...u, status: 'failed', timeLeft: 'Invalid upload data' } : u
        ));
        window.queuedUploadsData?.delete(nextUpload.id);
      }
    }
  }, [uploads]);

  return (
    <UploadQueueContext.Provider 
      value={{ 
        uploads, 
        addUpload, 
        addDebugUpload,
        updateUpload, 
        removeUpload, 
        retryUpload,
        clearCompleted
      }}
    >
      {children}
      
      {/* Global BackgroundUploader - only show on authenticated pages */}
      {showUploader && (uploads.length > 0 || isDevelopment) && (() => {
        // Don't show on auth pages
        const pathname = typeof window !== 'undefined' ? window.location.pathname : '';
        const isAuthPage = pathname.includes('/sign-in') || 
                          pathname.includes('/sign-up') || 
                          pathname.includes('/login') ||
                          pathname.includes('/register');
        
        if (isAuthPage) return null;
        
        return (
          <BackgroundUploader 
            uploads={uploads}
            onClose={() => setShowUploader(false)}
            onRetry={retryUpload}
            onOpenUploadModal={() => {
              console.log('Upload Another clicked - dispatching event to open upload modal');
              window.dispatchEvent(new Event('open-upload-modal'));
            }}
            onSimulateFailure={isDevelopment ? () => addDebugUpload('failed') : undefined}
            onSimulateCancellation={isDevelopment ? () => addDebugUpload('cancelled') : undefined}
          />
        );
      })()}
    </UploadQueueContext.Provider>
  );
}

// Custom hook to use the upload queue
export function useUploadQueue() {
  const context = useContext(UploadQueueContext);
  if (!context) {
    throw new Error('useUploadQueue must be used within UploadQueueProvider');
  }
  return context;
}